import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FUNC_GROUPS, FUNC_META, casesForFunc } from "@/data/useCases";
import { PRODUCTS, PRACTICES, INDUSTRIES_V2 } from "@/data/solutions";
import { useBookCall } from "@/components/BookCallContext";
import { TESTIDS } from "@/constants/testIds";

// Tab order per latest brief: Home · Practices · Products · Technology · Industries · Use Cases · About Us.
// Since there's no longer a "list" landing page for Practices/Products/Industries,
// clicking those tabs takes the user straight to the first entity's detail page.
// The mega-panel still opens on hover so the visitor can jump to any specific one.
const TABS = [
    { to: "/", label: "Home", testId: TESTIDS.nav.tabHome, end: true },
    { to: "/practices/customer-experience", label: "Practices", testId: TESTIDS.nav.tabPractices, mega: "practices", pathMatch: "/practices" },
    { to: "/products/kyro-engage", label: "Products", testId: TESTIDS.nav.tabProducts, mega: "products", pathMatch: "/products" },
    { to: "/technology", label: "Technology", testId: TESTIDS.nav.tabTech },
    { to: "/industries/travel", label: "Industries", testId: TESTIDS.nav.tabIndustries, mega: "industries", pathMatch: "/industries" },
    { to: "/use-cases/listen", label: "Use Cases", testId: TESTIDS.nav.tabUC, mega: "usecases", pathMatch: "/use-cases" },
    { to: "/about", label: "About Us", testId: TESTIDS.nav.tabAbout },
];

export default function Navigation() {
    const [megaKey, setMegaKey] = useState(null); // 'usecases' | 'products' | 'practices' | 'industries' | null
    const [activeFunc, setActiveFunc] = useState("listen"); // for use-cases panel
    const [mobileOpen, setMobileOpen] = useState(false);
    const navRef = useRef(null);
    const closeTimer = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { openModal } = useBookCall();

    // Hover-open: small delay on leave so we can move the mouse into the panel
    // without it flickering shut.
    const openMega = (key) => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setMegaKey(key);
        if (key === "usecases") setActiveFunc("listen");
    };
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setMegaKey(null), 140);
    };
    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    // Route change closes mega + mobile menu
    useEffect(() => {
        setMegaKey(null);
        setMobileOpen(false);
    }, [location.pathname]);

    // Cleanup timer on unmount
    useEffect(
        () => () => {
            if (closeTimer.current) clearTimeout(closeTimer.current);
        },
        []
    );

    const go = (path) => {
        setMegaKey(null);
        navigate(path);
    };

    return (
        <nav
            ref={navRef}
            data-testid={TESTIDS.nav.root}
            style={{
                position: "sticky",
                top: 0,
                zIndex: 60,
                background: "rgba(8,9,12,0.78)",
                backdropFilter: "blur(14px)",
                borderBottom: "1px solid var(--line)",
            }}
        >
            <div
                className="nav-in"
                style={{
                    maxWidth: "var(--maxw)",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    height: 64,
                    padding: "0 26px",
                }}
            >
                <Link
                    data-testid={TESTIDS.nav.logo}
                    to="/"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        textDecoration: "none",
                        color: "var(--ink)",
                        marginRight: 16,
                    }}
                >
                    <span
                        style={{
                            width: 22,
                            height: 22,
                            borderRadius: 7,
                            background: "linear-gradient(135deg,#3b78ff,#0e3bbf)",
                            boxShadow: "0 4px 14px rgba(26,86,219,0.4)",
                        }}
                    />
                    <span className="font-display" style={{ fontSize: "1.08rem" }}>
                        KyroLabs
                    </span>
                </Link>

                <div className="hidden lg:flex items-center gap-1">
                    {TABS.map((t) =>
                        t.mega ? (
                            <MegaButton
                                key={t.to}
                                to={t.to}
                                label={t.label}
                                testId={t.testId}
                                isActive={location.pathname.startsWith(t.pathMatch || t.to) && (t.pathMatch || t.to) !== "/"}
                                isOpen={megaKey === t.mega}
                                onOpen={() => openMega(t.mega)}
                                onClose={scheduleClose}
                            />
                        ) : (
                            <NavTab key={t.to} to={t.to} end={t.end} testId={t.testId}>
                                {t.label}
                            </NavTab>
                        )
                    )}
                </div>

                <span style={{ flex: 1 }} />

                <div className="hidden lg:flex items-center gap-2" style={{ marginLeft: 8 }}>
                    <button
                        data-testid={TESTIDS.nav.bookCall}
                        className="btn btn-p btn-sm"
                        onClick={() => openModal({ source: "nav" })}
                    >
                        Book a Demo <ArrowRight size={12} />
                    </button>
                </div>

                <button
                    className="lg:hidden nav-mobile-toggle"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Menu"
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        border: "1px solid var(--line-strong)",
                        background: "rgba(255,255,255,0.03)",
                        color: "var(--ink)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "auto",
                    }}
                >
                    {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {/* MEGA PANEL */}
            <AnimatePresence>
                {megaKey && (
                    <motion.div
                        key={megaKey}
                        data-testid={TESTIDS.nav.megaPanel}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        style={{
                            position: "absolute",
                            top: 60,
                            left: 26,
                            right: 26,
                            maxWidth: "var(--maxw)",
                            margin: "0 auto",
                            background: "#0e1116",
                            border: "1px solid var(--line-strong)",
                            borderRadius: 16,
                            boxShadow: "0 28px 70px rgba(0,0,0,0.65)",
                            display: "flex",
                            zIndex: 70,
                            overflow: "hidden",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        {megaKey === "usecases" && (
                            <UseCasesMega
                                activeFunc={activeFunc}
                                setActiveFunc={setActiveFunc}
                                onPick={go}
                            />
                        )}
                        {megaKey === "products" && (
                            <SimpleMega
                                label="Products"
                                items={PRODUCTS.map((p) => ({
                                    id: p.id,
                                    name: p.name,
                                    one: p.tagline,
                                    color: p.color,
                                }))}
                                itemPath={(id) => `/products/${id}`}
                                onPick={go}
                                testIdItem={(id) => TESTIDS.products.card(id)}
                            />
                        )}
                        {megaKey === "practices" && (
                            <SimpleMega
                                label="Practices"
                                items={PRACTICES.map((p) => ({
                                    id: p.id,
                                    name: p.name,
                                    one: p.short,
                                    color: p.color,
                                }))}
                                itemPath={(id) => `/practices/${id}`}
                                onPick={go}
                                testIdItem={(id) => TESTIDS.practices.card(id)}
                            />
                        )}
                        {megaKey === "industries" && (
                            <SimpleMega
                                label="Industries"
                                items={INDUSTRIES_V2.map((i) => ({
                                    id: i.id,
                                    name: i.name,
                                    one: i.short,
                                    color: i.color,
                                }))}
                                itemPath={(id) => `/industries/${id}`}
                                onPick={go}
                                testIdItem={(id) => TESTIDS.industries.card(id)}
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mobile sheet */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="lg:hidden"
                        style={{
                            background: "#0e1116",
                            borderTop: "1px solid var(--line)",
                            borderBottom: "1px solid var(--line)",
                            padding: 16,
                        }}
                    >
                        <div style={{ display: "grid", gap: 6 }}>
                            {TABS.map((t) => (
                                <Link
                                    key={t.to}
                                    to={t.to}
                                    style={mobileLink}
                                    onClick={() => setMobileOpen(false)}
                                >
                                    {t.label}
                                </Link>
                            ))}
                            <a href={`mailto:hello@kyrolabs.ai`} style={mobileLink}>
                                hello@kyrolabs.ai
                            </a>
                            <button
                                className="btn btn-p"
                                style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
                                onClick={() => {
                                    setMobileOpen(false);
                                    openModal({ source: "nav-mobile" });
                                }}
                            >
                                Book a Demo <ArrowRight size={14} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}

/* ---------- pieces ---------- */

function NavTab({ to, end, testId, children }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testId}
            style={({ isActive }) => tabBtnStyle(isActive)}
        >
            {children}
        </NavLink>
    );
}

function MegaButton({ to, label, testId, isActive, isOpen, onOpen, onClose }) {
    const navigate = useNavigate();
    return (
        <button
            data-testid={testId}
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            onFocus={onOpen}
            onClick={() => navigate(to)}
            style={tabBtnStyle(isActive || isOpen)}
        >
            {label}
            <ChevronDown
                size={13}
                style={{
                    marginLeft: 2,
                    transition: "transform .18s ease",
                    transform: isOpen ? "rotate(180deg)" : "none",
                }}
            />
        </button>
    );
}

/* USE CASES — 2-column mega (function rail + cards) — grouped by function. */
function UseCasesMega({ activeFunc, setActiveFunc, onPick }) {
    const ucs = casesForFunc(activeFunc);
    const groupName = FUNC_META[activeFunc]?.name || "";
    return (
        <>
            <div
                className="mega-rail"
                style={{
                    width: 260,
                    background: "#08090c",
                    borderRight: "1px solid var(--line)",
                    padding: 14,
                    flexShrink: 0,
                }}
            >
                <div style={railHeadStyle}>
                    <span className="font-mono-label" style={{ color: "var(--dim)" }}>
                        By function
                    </span>
                </div>
                {FUNC_GROUPS.map((g) => (
                    <button
                        key={g.id}
                        data-testid={TESTIDS.nav.megaIndustry(g.id)}
                        className={`mega-ind ${activeFunc === g.id ? "active" : ""}`}
                        onMouseEnter={() => setActiveFunc(g.id)}
                        onFocus={() => setActiveFunc(g.id)}
                        onClick={() => onPick(`/use-cases/${g.id}`)}
                    >
                        {g.name}
                        <span style={{ opacity: activeFunc === g.id ? 1 : 0, transition: ".14s" }}>→</span>
                    </button>
                ))}
            </div>
            <div style={{ flex: 1, padding: "22px 24px", minHeight: 260, maxHeight: 460, overflowY: "auto" }}>
                <div className="font-mono-label" style={{ color: "var(--dim)", marginBottom: 14 }}>
                    {groupName}
                </div>
                <div style={cardsGridStyle}>
                    {ucs.map((u) => (
                        <MegaCard
                            key={u.id}
                            testId={TESTIDS.nav.megaCard(u.id)}
                            title={u.name}
                            body={u.one}
                            onClick={() => onPick(`/use-cases/${u.id}`)}
                        />
                    ))}
                </div>
            </div>
        </>
    );
}

/* PRODUCTS / PRACTICES / INDUSTRIES — single wide cards grid.
   No sub-rail; clicking a card navigates to its detail page. There's no
   "See all", the mega always shows every entity. */
function SimpleMega({ label, items, itemPath, onPick, testIdItem }) {
    return (
        <div style={{ flex: 1, padding: "22px 24px", minHeight: 260 }}>
            <div style={railHeadStyleFull}>
                <span className="font-mono-label" style={{ color: "var(--dim)" }}>
                    {label}
                </span>
            </div>
            <div style={cardsGridStyleWide}>
                {items.map((it) => (
                    <MegaCard
                        key={it.id}
                        testId={testIdItem ? testIdItem(it.id) : undefined}
                        title={it.name}
                        body={it.one}
                        accent={it.color}
                        onClick={() => onPick(itemPath(it.id))}
                    />
                ))}
            </div>
        </div>
    );
}

function MegaCard({ testId, title, body, accent, onClick }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            style={{
                textAlign: "left",
                background: "rgba(255,255,255,0.02)",
                border: "1px solid var(--line)",
                borderRadius: 10,
                padding: "12px 14px",
                cursor: "pointer",
                transition: ".16s",
                font: "inherit",
                color: "var(--ink)",
                // Top-align content so titles line up across a row even when
                // some cards have longer descriptions that wrap.
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: 4,
                minHeight: 76,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = accent || "var(--accent)";
                e.currentTarget.style.background = "var(--accent-soft)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--line)";
                e.currentTarget.style.background = "rgba(255,255,255,0.02)";
            }}
        >
            <b
                className="font-display"
                style={{ display: "block", marginBottom: 2, fontWeight: 600, fontSize: ".9rem" }}
            >
                {title}
            </b>
            <span style={{ color: "var(--kl-muted)", fontSize: ".78rem" }}>{body}</span>
        </button>
    );
}

/* ---------- styles ---------- */

const tabBtnStyle = (isActive) => ({
    background: isActive ? "rgba(255,255,255,0.04)" : "none",
    border: "none",
    fontFamily: "inherit",
    fontSize: ".86rem",
    color: isActive ? "var(--accent)" : "var(--kl-muted)",
    padding: "9px 12px",
    borderRadius: 9,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    textDecoration: "none",
    transition: ".18s",
    whiteSpace: "nowrap",
});

const upfrontStyle = {
    fontFamily: "JetBrains Mono, monospace",
    fontSize: ".74rem",
    color: "var(--kl-muted)",
    textDecoration: "none",
    padding: "7px 10px",
    borderRadius: 999,
    border: "1px solid var(--line-strong)",
    background: "rgba(255,255,255,0.02)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: ".18s",
};

const mobileLink = {
    padding: "10px 12px",
    borderRadius: 9,
    color: "var(--ink)",
    textDecoration: "none",
    fontSize: ".95rem",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--line)",
    display: "block",
};

const railHeadStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    padding: "0 6px 10px",
    borderBottom: "1px solid var(--line)",
};

const railHeadStyleFull = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: "1px solid var(--line)",
};

const seeAllStyle = {
    background: "none",
    border: "none",
    color: "var(--accent)",
    font: "inherit",
    cursor: "pointer",
    fontSize: ".78rem",
};

const cardsGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
};

const cardsGridStyleWide = {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
};
